import { useState, useEffect } from "react";
import Head from "next/head";
import classNames from "classnames/bind";
import ZoomCross24PxOutlinedIcon from "@JohnLewisPartnership/jl-design-system/dist/elements/icons-jb/ZoomCross24PxOutlined";
import { GalleryObject, ProductImages, getSortedGalleryItems } from "../utils";
import { Video } from "../../../types/prodcatBaseTypes";
import styles from "./CarouselGallery.module.scss";
import { Carousel } from "../components/Carousel/Carousel";
import { dispatchGalleryMonetateQEvent } from "../components/Analytics/dispatchGalleryMonetateQEvent";
import { imageProfileGenerator } from "../../../utils/image/imageProfileGenerator";
import { SocialProofMessages } from "../components/SocialProofMessages/SocialProofMessages";
import { ZoomModal } from "../components/ZoomModal/ZoomModal";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";

const cx = classNames.bind(styles);

type Props = {
  title: string;
  images: ProductImages;
  videos: Video[];
  sizes: string;
  hasSocialProofMessagesExperience?: boolean;
  socialProofMessagesProperties?: {
    productId: string;
    brand: string;
    availableToOrder: boolean;
    appId: string;
    withClose?: boolean;
  };
  hasImmediateStateUpdates?: boolean;
  hasTransparentThumbnails?: boolean;
  hasDeferredMonetateQEvents?: boolean;
  hasDeferredIntersectionObserverCallback?: boolean;
  hasSuperZoom?: boolean;
};

export const Counter = ({
  fullscreen,
  selectedInd,
  itemCount,
}: {
  fullscreen?: boolean;
  selectedInd: number;
  itemCount: number;
}) => {
  return (
    <div
      className={cx(styles.counter, {
        "counter--fullscreen": fullscreen,
      })}
    >
      {selectedInd + 1} / {itemCount}
    </div>
  );
};

export const CarouselGalleryUI = ({
  title,
  images,
  videos,
  sizes,
  hasSocialProofMessagesExperience,
  socialProofMessagesProperties,
  hasImmediateStateUpdates,
  hasTransparentThumbnails,
  hasDeferredMonetateQEvents,
  hasDeferredIntersectionObserverCallback,
  hasSuperZoom,
}: Props) => {
  const [selectedInd, setSelectedInd] = useState(0);
  const [initialised, setInitialised] = useState(false);
  const [modalSelectedInd, setModalSelectedInd] = useState(0);
  const [zoomModalActive, setZoomModalActive] = useState(false);
  const outputImages = [images.primary, ...images.alternatives].map((image) => {
    return {
      type: "image",
      imageUrl: image.split("?")[0],
      title,
      thumbnailUrl: `${image}?wid=120&hei=160`,
    };
  }) as GalleryObject[];

  const outputVideos = videos.map((video) => {
    const videoThumbnail = video.sources.find(
      (source) => source.key === "thumbnail",
    );

    const videoThumbnailBaseUrl = videoThumbnail ? videoThumbnail.url : "";

    const { baseUrl } = getBaseUrlAndQuery(videoThumbnailBaseUrl);

    const videoTumbnailUrlWithParams = `${baseUrl}?wid=120&hei=160`;

    return {
      type: "video",
      imageUrl: baseUrl,
      thumbnailUrl: videoTumbnailUrlWithParams,
      videoUrl: video.sources.find((source) => source.key === "stream")?.url,
      posterUrl: video.sources.find((source) => source.key === "poster")?.url,
      title,
    };
  }) as GalleryObject[];

  const mediaItems = getSortedGalleryItems<GalleryObject>(
    outputImages,
    outputVideos,
  );

  const showZoomModal = () => {
    const currentImageIndex = outputImages.findIndex(
      ({ imageUrl }) => mediaItems[selectedInd]?.imageUrl === imageUrl,
    );
    setModalSelectedInd(currentImageIndex);
    setZoomModalActive(true);
    dispatchGalleryMonetateQEvent("ZoomIcon");
  };

  const closeZoomModal = () => {
    setZoomModalActive(false);
    dispatchGalleryMonetateQEvent("CloseModal", "imageModal");
  };

  useEffect(() => {
    setInitialised(true);
  }, []);

  return (
    <>
      <Head>
        <link
          rel="preload"
          as="image"
          href={images.primary}
          imageSrcSet={imageProfileGenerator(images.primary)}
          imageSizes={sizes}
        />
      </Head>
      <Carousel
        mediaItems={mediaItems}
        selectedInd={selectedInd}
        setSelectedInd={setSelectedInd}
        imageSizes={sizes}
        initialised={initialised}
        hasImmediateStateUpdates={hasImmediateStateUpdates}
        hasTransparentThumbnails={hasTransparentThumbnails}
        hasDeferredMonetateQEvents={hasDeferredMonetateQEvents}
        hasDeferredIntersectionObserverCallback={
          hasDeferredIntersectionObserverCallback
        }
        hasSuperZoom={hasSuperZoom}
      >
        {hasSocialProofMessagesExperience && socialProofMessagesProperties && (
          <SocialProofMessages {...socialProofMessagesProperties} />
        )}
        {mediaItems.length > 1 && (
          <Counter selectedInd={selectedInd} itemCount={mediaItems.length} />
        )}
        {mediaItems[selectedInd]?.type === "image" && (
          <button
            className={styles.zoomButton}
            onClick={showZoomModal}
            data-testid="gallery:zoom:button"
          >
            <ZoomCross24PxOutlinedIcon className={styles.zoomIcon} />
            Zoom
          </button>
        )}
      </Carousel>
      {zoomModalActive && (
        <ZoomModal
          closeZoomModal={closeZoomModal}
          mediaItems={outputImages}
          selectedIndex={modalSelectedInd}
          setSelectedIndex={setModalSelectedInd}
          hasImmediateStateUpdates={hasImmediateStateUpdates}
          hasDeferredMonetateQEvents={hasDeferredMonetateQEvents}
          hasDeferredIntersectionObserverCallback={
            hasDeferredIntersectionObserverCallback
          }
          hasSuperZoom={hasSuperZoom}
        />
      )}
    </>
  );
};
